fragment SupportTicketFields on SupportTicket {
  id
  number
  title
  titleSource
  statusKey
  statusName
  statusCategory
  priority
  channel
  source
  requesterRole
  tags
  aiState
  aiAlongsideAgent
  resolution
  closeReason
  summary
  firstResponseAt
  resolvedAt
  closedAt
  lastMessageAt
  createdAt
  unreadForMe
  version
  type {
    id
    name
    icon
  }
  assignee {
    id
    name
    avatarUrl
  }
  team {
    id
    name
    archived
  }
  requester {
    id
    externalUserId
    name
    email
    role
    plan
    lastSeenAt
    attributes {
      key
      value
    }
  }
  organization {
    id
    name
  }
}

fragment SupportMessageFields on SupportMessage {
  id
  ticketId
  kind
  authorType
  author {
    id
    name
    avatarUrl
  }
  body
  mentions {
    id
    name
    avatarUrl
  }
  attachments {
    id
    filename
    mimeType
    size
    url
    previewUrl
  }
  citations {
    title
    url
    anchor
    quote
  }
  images {
    url
    alt
  }
  createdAt
}

fragment TicketEventFields on TicketEvent {
  id
  seq
  eventType
  visibility
  actor {
    type
    id
    name
  }
  field
  from
  to
  messageId
  createdAt
}

fragment SupportEndUserFields on SupportEndUser {
  id
  externalUserId
  name
  email
  role
  roleKeys
  imported {
    source
    role
    at
    attributes {
      key
      value
    }
  }
  importedRoleKeys
  plan
  lastSeenAt
  profileUpdatedAt
  createdAt
  ticketCount
  emailWhenOffline
  organization {
    id
    name
  }
  contact {
    id
    name
    email
  }
  attributes {
    key
    value
  }
}

fragment WebhookEndpointFields on WebhookEndpoint {
  id
  url
  events
  enabled
  failureCount
  disabledAt
  disabledReason
  lastSuccessAt
  createdAt
  deliveredLastDay
  failedLastDay
  pending
  lastError
  lastErrorAt
}

fragment SupportSandboxFields on SupportSandbox {
  tenantId
  name
  createdAt
  ticketCount
  endUserCount
  roles {
    key
    displayName
    match {
      source
      attributeKey
      values
    }
  }
}

fragment WorkItemLinkFields on WorkItemLink {
  id
  provider
  externalId
  customId
  url
  title
  statusName
  statusColor
  isDone
  assignees {
    id
    name
  }
  dueDate
  listName
  linkMode
  stageKey
  syncedAt
  syncError
}

fragment SupportImportRunFields on SupportImportRun {
  id
  status
  region
  sinceDate
  pagesDone
  lastError
  startedAt
  finishedAt
  createdAt
  counters {
    conversationsSeen
    ticketsCreated
    ticketsSkipped
    messagesCreated
    contactsLinked
    contactsAmbiguous
    conversationsSkippedErased
    contactsCreated
    failed
  }
  failures {
    externalId
    reason
    at
  }
}

fragment SupportCompareRateFields on SupportCompareRate {
  successes
  n
  rate
  low
  high
}

fragment SupportCompareRunFields on SupportCompareRun {
  id
  status
  from
  to
  sampleSize
  candidates
  answerModel
  graderModel
  rewriteQueries
  aiCostUsd
  lastError
  startedAt
  finishedAt
  createdAt
  counters {
    sampled
    processed
    answered
    handover
    errors
    noReference
    noRole
    noQuestion
    graded
    ungraded
    wouldResolve
    partial
    incorrect
  }
}

fragment SupportCompareMetricsFields on SupportCompareMetrics {
  sampled
  processed
  graded
  ungraded
  errors
  noReference
  noRole
  noQuestion
  revvly {
    ...SupportCompareRateFields
  }
  revvlyWhereFinTookPart {
    ...SupportCompareRateFields
  }
  revvlyAnsweredWhereFinTookPart {
    ...SupportCompareRateFields
  }
  fin {
    ...SupportCompareRateFields
  }
  finConfirmed {
    ...SupportCompareRateFields
  }
  partialRate
  incorrectRate
  handoverRate
  finParticipated
  finNotParticipated
  finUnknown
  headToHead {
    both
    onlyRevvly
    onlyFin
    neither
    total
  }
  verdicts {
    wouldResolve
    partial
    incorrect
    handover
    ungraded
    error
    noReference
    noRole
    noQuestion
  }
  aiCostUsd
  costPerProjectedResolutionUsd
  smallSample
  finSmallSample
  minGradedForComparison
}

fragment SupportComparePatternsFields on SupportComparePatterns {
  diagnosed
  undiagnosed
  resolved
  notResolved
  gaps {
    foundNotUsed
    notFound
    hiddenByRole
    notIndexed
    missingArticle
    noFinSource
  }
  reasons {
    nothingRetrieved
    noAnswer
    uncited
    noRole
    error
  }
  segments {
    dimension
    label
    resolved
    n
    rate
  }
  medianTopScoreResolved
  medianTopScoreNotResolved
  finArticles {
    title
    url
    articleId
    status
    conversations
    retrieved
  }
}

fragment SupportCompareResultFields on SupportCompareResult {
  id
  ticketId
  ticketNumber
  title
  conversationAt
  analyzedAt
  sampleIndex
  question
  earlierTurns
  fin {
    participated
    resolutionState
    sources {
      title
      url
    }
    rating
    ratingRemark
    conversationRating
    answer
  }
  reader {
    source
    intercomRole
    roles {
      key
      name
    }
    attributes {
      key
      value
    }
  }
  referenceKind
  referenceText
  revvlyOutcome
  revvlyReason
  revvlyReasonCode
  revvlyDraft
  revvlyCitations {
    articleId
    title
    url
  }
  verdict
  gap
  diagnostics {
    searchQuery
    rewrittenQuery
    questionWords
    retrieved {
      articleId
      title
      url
      score
      cited
    }
    finSources {
      title
      url
      articleId
      articleTitle
      status
      retrieved
    }
    guidelines {
      guidelineId
      kind
      title
      body
      cited
    }
  }
  graderReason
  costUsd
}
